import {
  ExIcon,
  IconSize,
  ExLink,
  LinkSize,
  ExIconButton,
  IconButtonType,
  IconButtonFlavor,
  IconButtonSize,
} from '@boomi/exosphere';
import { useTranslation } from 'react-i18next';
import { ModelsMetrics, ModelMetricsCardProps } from '../types/models.types';
import './ModelsMetricsRow.scss';
import boomiLogo from '@assets/boomi-ai/ui-kit/agentFoundation/boomi-favicon.png';
import anthropicLogo from '@assets/boomi-ai/ui-kit/agentFoundation/anthropic-logo.png';
import geminiLogo from '@assets/boomi-ai/ui-kit/agentFoundation/gemini-logo.png';

interface ModelsMetricsRowProps {
  metrics: ModelsMetrics;
  onBoomManagedClick?: () => void;
}

interface MetricsCardExtendedProps extends ModelMetricsCardProps {
  tooltip?: string;
}

function MetricsCard({
  title,
  count,
  total,
  icon,
  image,
  isLink = false,
  linkText,
  onLinkClick,
  tooltip,
}: Readonly<MetricsCardExtendedProps>) {
  return (
    <div className="metrics-card">
      <div className="metrics-card__container">
        {(icon || image) && (
          <div className="metrics-card__icon-wrapper">
            {image ? (
              <img src={image} alt={title} className="metrics-card__image" />
            ) : (
              <ExIcon icon={icon} className="metrics-card__icon" hideBrowserTooltip size={IconSize.M} />
            )}
          </div>
        )}
        <div className="metrics-card__content">
          <div className="metrics-card__header">
            <span className="metrics-card__title">{title}</span>
            {tooltip && (
              <ExIconButton
                slot="anchor"
                icon="Information"
                hideBrowserTooltip
                type={IconButtonType.TERTIARY}
                flavor={IconButtonFlavor.BRANDED}
                size={IconButtonSize.XSMALL}
                label="Information"
                circular
                tooltipText={tooltip}
              />
            )}
          </div>
          <div className="metrics-card__value">
            <span className="metrics-card__count">
              {count}
              {total && (
                <span className="metrics-card__total">
                  {' '}
                  of
                  {' '}
                  {total}
                </span>
              )}
            </span>
            {isLink && linkText && (
              <ExLink
                className="metrics-card__link"
                size={LinkSize.SMALL}
                onClick={onLinkClick}
              >
                {linkText}
              </ExLink>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ModelsMetricsRow({
  metrics,
  onBoomManagedClick,
}: Readonly<ModelsMetricsRowProps>) {
  const [t] = useTranslation('common');
  return (
    <div className="models-metrics-row">
      <MetricsCard
        title="Total Active LLMs"
        count={metrics.activeLLMs}
        total={metrics.totalLLMs}
        icon="CubeAlt"
        tooltip="Total number of active LLMs across Boomi-managed and registered models."
      />
      <MetricsCard
        title={t('boomi_managed')}
        count={metrics.boomManagedLLMs}
        isLink
        linkText="View Models"
        onLinkClick={onBoomManagedClick}
        image={boomiLogo}
        tooltip="Models hosted and managed by Boomi. These cannot be edited or deleted."
      />
      <MetricsCard
        title="Anthropic"
        count={metrics.anthropicLLMs}
        image={anthropicLogo}
        tooltip="Registered Anthropic models available for agent deployments."
      />
      <MetricsCard
        title="Gemini"
        count={metrics.geminiLLMs}
        image={geminiLogo}
        tooltip="Registered Gemini models available for agent deployments."
      />
    </div>
  );
}

export default ModelsMetricsRow;
